<template>
  <CDialog
    @outer-click="close"
    :show="show"
    :custom-class="'max-w-[900px]'"
    :title="$t('create_bot')"
    @close="close"
  >
    <div class="flex items-start flex-col md:flex-row p-5 text-center gap-4">
      <PlanInfo
        :strategies="plan?.allowed_strategies"
        :coins="coinsList"
        :commissionPercent="plan?.commission_percent"
        :plan="plan?.title"
      />
      <div class="w-full space-y-2">
        <FGroup :label="$t('exchanges')">
          <div class="flex-y-center gap-2 w-full">
            <FSelect
              :options="exchanges"
              v-model="form.values.exchange"
              :error="form.$v.value.exchange?.$error"
              :placeholder="$t('choose_exchange')"
              label-key="title"
              selected-styles="text-left"
              value-key="id"
              class="w-full"
            />
            <RouterLink to="/exchanges" v-if="!exchanges?.length">
              <CButton
                variant="transparent"
                text=""
                class="w-max flex-y-center !h-10 !px-2.5"
                icon="icon-add"
              />
            </RouterLink>
          </div>
        </FGroup>
        <FGroup :label="t('coin_assigned', { count: 'USDT' })">
          <template #labelOpposite v-if="isDesktop">
            <p class="text-xs text-gray mr-auto ml-1">
              ({{ $t("tradable_balance") }})
            </p>
            <div class="flex-y-center gap-16">
              <span class="text-dark-100 text-sm font-normal">
                {{ $t("available_bot_balance") }}
              </span>
              <span
                class="pr-10 text-dark-100 text-sm font-normal"
                :class="{ 'pr-[88px]': form.values.exchange }"
              >
                {{ $t("total_bot_balance") }}
              </span>
            </div>
          </template>
          <div class="flex-y-center gap-2 w-full">
            <FInput
              autocomplete="off"
              placeholder="10 000"
              v-maska="moneyMask"
              :error="form.$v.value.money?.$error || isCheck || isLowMoney"
              v-model="form.values.money"
              class="!pr-0 !h-10 lg:!h-12"
              suffix-class="pr-1"
              @focus="handleInputFocus"
            >
              <template #suffix>
                <div class="flex-y-center gap-1">
                  <template v-if="checkExchangeBalance?.length">
                    <button
                      v-if="!showMaxAmount"
                      @click.stop.prevent="useMaxMoney"
                      class="bg-gray-380/40 px-2 py-1 text-sm rounded-md flex-y-center justify-center"
                    >
                      <span>Max</span>
                    </button>
                    <button
                      v-else
                      @click.stop.prevent="removeMaxMoney"
                      class="bg-gray-380/40 px-2 py-1 text-sm rounded-md flex-y-center justify-center"
                    >
                      <span class="icon-close text-dark text-base" />
                    </button>
                  </template>
                  <div class="flex-y-center" v-if="isDesktop">
                    <div
                      v-if="checkExchangeBalance?.length"
                      :key="isRefreshed"
                      class="flex-y-center"
                    >
                      <div class="flex-y-center rounded-lg overflow-hidden">
                        <div
                          v-for="balance in checkExchangeBalance"
                          :key="balance?.id"
                          class="flex-center min-w-[120px] px-3 w-fit h-10 bg-[#EEEFF3] border-r border-gray-50"
                        >
                          <p
                            v-if="checkExchangeBalance?.length"
                            class="text-sm leading-130 text-dark-100"
                            :class="{ 'refreshed-animation': !isRefreshed }"
                          >
                            {{
                              (
                                Number(balance?.available_usdt_balance) ?? 0
                              )?.toFixed(3)
                            }}
                          </p>
                          <p v-else class="text-sm leading-130 text-dark-100">
                            0,00
                          </p>
                        </div>
                        <div
                          v-for="balance in checkExchangeBalance"
                          :key="balance?.id"
                          class="flex-center min-w-[120px] px-3 w-fit h-10 bg-[#EEEFF3]"
                        >
                          <p
                            v-if="checkExchangeBalance?.length"
                            :class="{ 'refreshed-animation': !isRefreshed }"
                            class="text-sm leading-130 text-dark-100 transition-200"
                          >
                            {{
                              (Number(balance?.usdt_balance) ?? 0)?.toFixed(3)
                            }}
                          </p>
                          <p v-else class="text-sm leading-130 text-dark-100">
                            0,00
                          </p>
                        </div>
                      </div>
                    </div>
                    <div
                      v-else
                      class="flex-y-center rounded-lg overflow-hidden"
                    >
                      <div
                        class="flex-center min-w-[120px] px-3 w-fit h-10 bg-[#EEEFF3] border-r border-gray-50"
                      >
                        <p class="text-sm leading-130 text-dark-100">0,00</p>
                      </div>
                      <div
                        class="flex-center min-w-[120px] px-3 w-fit h-10 bg-[#EEEFF3]"
                      >
                        <p class="text-sm leading-130 text-dark-100">0,00</p>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </FInput>
            <Transition name="fade" mode="in-out">
              <CRefreshButton
                v-if="form.values.exchange"
                @refresh-bots="refreshBalance"
                :is-refreshed="isRefreshed"
              />
            </Transition>
          </div>
          <div class="w-full" v-if="isMobile">
            <div class="w-full flex-y-center">
              <span
                class="w-full text-center text-dark-100 text-sm font-normal"
              >
                {{ $t("available_bot_balance") }}
              </span>
              <span
                class="w-full text-center text-dark-100 text-sm font-normal"
              >
                {{ $t("total_bot_balance") }}
              </span>
            </div>
            <div class="w-full rounded-lg overflow-hidden">
              <div
                v-if="checkExchangeBalance?.length"
                :key="isRefreshed"
                class="w-full"
              >
                <div class="flex-y-center rounded-lg overflow-hidden">
                  <div
                    v-for="balance in checkExchangeBalance"
                    :key="balance?.id"
                    class="w-full flex-center min-w-[120px] px-3 h-10 bg-[#EEEFF3] border-r border-gray-50"
                  >
                    <p
                      v-if="checkExchangeBalance?.length"
                      class="text-sm leading-130 text-dark-100"
                      :class="{ 'refreshed-animation': !isRefreshed }"
                    >
                      {{
                        (Number(balance?.available_usdt_balance) ?? 0)?.toFixed(
                          3,
                        )
                      }}
                    </p>
                    <p v-else class="text-sm leading-130 text-dark-100">0,00</p>
                  </div>
                  <div
                    v-for="balance in checkExchangeBalance"
                    :key="balance?.id"
                    class="w-full flex-center min-w-[120px] px-3 h-10 bg-[#EEEFF3]"
                  >
                    <p
                      v-if="checkExchangeBalance?.length"
                      :class="{ 'refreshed-animation': !isRefreshed }"
                      class="text-sm leading-130 text-dark-100 transition-200"
                    >
                      {{ (Number(balance?.usdt_balance) ?? 0)?.toFixed(3) }}
                    </p>
                    <p v-else class="text-sm leading-130 text-dark-100">0,00</p>
                  </div>
                </div>
              </div>
              <div v-else class="w-full flex-y-center">
                <div
                  class="w-full flex-center min-w-[120px] px-3 h-10 bg-[#EEEFF3] border-r border-gray-50"
                >
                  <p class="text-sm leading-130 text-dark-100">0,00</p>
                </div>
                <div
                  class="w-full flex-center min-w-[120px] px-3 h-10 bg-[#EEEFF3]"
                >
                  <p class="text-sm leading-130 text-dark-100">0,00</p>
                </div>
              </div>
            </div>
          </div>
        </FGroup>
        <CButton
          :disabled="loading"
          :loading="loading"
          size="sm"
          class="!w-full !mt-4 md:!mt-2 !text-sm md:!text-base"
          :text="$t('create_title')"
          @click="createBot"
        />
      </div>
    </div>
  </CDialog>
</template>

<script setup lang="ts">
import CDialog from "@/components/Common/Dialog/CDialog.vue";
import { computed, ref, watch } from "vue";
import { useForm } from "@/composables/useForm";
import { required } from "@vuelidate/validators";
import ApiService from "@/services/ApiService";
import { Exchange } from "@/types/common";
import { debounce, formatMoneyDecimal, moneyMask } from "@/utils";
import FGroup from "@/components/Form/FGroup.vue";
import FInput from "@/components/Form/Input/FInput.vue";
import FSelect from "@/components/Form/Select/FSelect.vue";
import CButton from "@/components/Common/CButton.vue";
import { useI18n } from "vue-i18n";
import { useCustomToast } from "@/composables/useCustomToast";
import PlanInfo from "@/modules/Bots/components/PlanInfo.vue";
import { useAuthStore } from "@/modules/Auth/stores";
import { useMobile } from "@/composables/useMobile";
import CRefreshButton from "@/components/Common/CRefreshButton.vue";

interface Props {
  show: boolean;
}
defineProps<Props>();
const emit = defineEmits<{
  (e: "close"): void;
  (e: "createBot", { exchange: number, assigned_money: number }): void;
}>();
const { isMobile } = useMobile("mobile");
const { isDesktop } = useMobile("desktop");

const { t } = useI18n();
const { showToast } = useCustomToast();

const userStore = useAuthStore();
const loading = ref(false);
const isRefreshed = ref(false);

const exchanges = ref<Exchange[]>([]);

const isLowMoney = ref(false);
const showMaxAmount = ref(false);

const isCheck = ref(false);
const validateMoney = ref(true);
const available_usdt_Balance = ref(0);

const form = useForm(
  {
    exchange: "",
    money: "",
  },
  {
    exchange: { required },
    money: { required },
  },
);

const close = () => {
  emit("close");
  form.values.exchange = "";
  form.values.money = "";
  form.$v.value.$reset();
  isCheck.value = false;
  isLowMoney.value = false;
};

const plan = computed(() => userStore?.user?.plan);
const min_assign_money = computed(() =>
  Number(userStore?.user?.plan?.min_assign_money),
);

const useMaxMoney = () => {
  showMaxAmount.value = true;
  form.values.money = String(available_usdt_Balance.value)?.split(".")?.[0];
};
const removeMaxMoney = () => {
  showMaxAmount.value = false;
  form.values.money = "";
};

const createBot = () => {
  form.$v.value.$touch();
  if (form.$v.value.$invalid || isCheck.value || isLowMoney.value) {
    return;
  }
  const data = {
    exchange: form.values.exchange,
    assigned_money: Number(form.values.money.replace(/\s/g, "")),
  };
  emit("createBot", data);
};

function getExchanges() {
  ApiService.get("/bot/exchange/").then((res) => {
    exchanges.value = res.data?.results?.map((exchange) => {
      return {
        ...exchange,
        icon: `/${exchange?.type}-logo.svg`,
      };
    });
  });
}
getExchanges();

const refreshBalance = () => {
  isRefreshed.value = true;
  ApiService.post(`/bot/exchange/refresh/${form.values.exchange}/`)
    .then((res) => {
      form.values.exchange = res?.data?.id;
      exchanges.value = exchanges.value.map((el) =>
        el?.id === res?.data?.id ? res?.data : el,
      );
      available_usdt_Balance.value = Number(
        checkExchangeBalance.value[0]?.available_usdt_balance?.replace(
          /\s/g,
          "",
        ),
      );
    })
    .catch((err: any) => {
      console.log(err, "err");
    })
    .finally(() => {
      setTimeout(() => {
        isRefreshed.value = false;
      }, 300);
    });
};

const checkExchangeBalance = computed(() => {
  return exchanges.value?.filter(
    (item) => item?.id === Number(form.values.exchange),
  );
});

const coinsList = computed(() => {
  return plan.value?.unique_coins?.length > 5
    ? {
        list: plan.value?.unique_coins?.slice(0, 5),
        element: plan.value?.unique_coins?.length - 5,
      }
    : {
        list: plan.value?.unique_coins,
        element: 0,
      };
});

const handleInputFocus = () => {
  if (!form.values.exchange) {
    showToast(t("select_exchange_first"), "warning");
  }
};

watch(
  () => form.values.money,
  (val) => {
    isCheck.value = false;
    if (val?.length === 0 && showMaxAmount.value) {
      showMaxAmount.value = false;
    }
    if (val?.length !== 0) {
      debounce("money", () => {
        const moneyValue = Number(val.replace(/\s/g, ""));
        isCheck.value = moneyValue > available_usdt_Balance.value;
        isLowMoney.value = moneyValue < min_assign_money.value;
        validateMoney.value = isCheck.value || isLowMoney.value;
      });
    }
  },
  { immediate: true, deep: true },
);
watch(
  () => form.values.exchange,
  () => {
    if (form.values.money) {
      form.values.money = "";
      isCheck.value = false;
      isLowMoney.value = false;
    }
    available_usdt_Balance.value = Number(
      checkExchangeBalance.value[0]?.available_usdt_balance?.replace(/\s/g, ""),
    );
  },
  { immediate: true, deep: true },
);
watch(
  () => isCheck.value,
  (val) => {
    if (val) {
      showToast(t("balance_enough_funds"), "error");
    }
  },
  { immediate: true, deep: true },
);
watch(
  () => isLowMoney.value,
  (val) => {
    if (val && form.values.money?.length) {
      showToast(
        t("min_funds", {
          money: formatMoneyDecimal(min_assign_money.value, 0),
        }),
        "error",
      );
    }
  },
  { immediate: true, deep: true },
);
</script>

<style scoped>
.refreshed-animation {
  animation: refreshed 500ms ease-in reverse;
}
@keyframes refreshed {
  0% {
    color: #080a15;
  }
  50% {
    color: #16cc53;
  }
  100% {
    color: #080a15;
  }
}
</style>
